import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  InputAdornment,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import { 
  GetApp, 
  Refresh, 
  Assessment,
  Search,
  Info,
  CheckCircle,
  Warning,
  CalendarToday,
  Person,
  TrendingUp,
  Group,
  Business,
  Description
} from '@mui/icons-material';

const Direct = () => {
  const [formData, setFormData] = useState({
    loginId: '',
    joiningFrom: '',
    joiningTo: '',
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});

  // Sample login IDs (you can replace with actual data from your system)
  const loginIds = [
    'john.smith',
    'sarah.johnson',
    'michael.brown',
    'emily.davis',
    'david.wilson',
    'lisa.anderson',
    'robert.taylor',
    'jennifer.white',
    'james.miller',
    'maria.garcia',
  ];

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.loginId) {
      newErrors.loginId = 'Please select a Login ID';
    }
    if (!formData.joiningFrom) {
      newErrors.joiningFrom = 'Please select From date';
    }
    if (!formData.joiningTo) {
      newErrors.joiningTo = 'Please select To date';
    }

    // Validate date range
    if (formData.joiningFrom && formData.joiningTo) {
      const fromDate = new Date(formData.joiningFrom);
      const toDate = new Date(formData.joiningTo);
      
      if (fromDate > toDate) {
        newErrors.joiningTo = 'To date cannot be earlier than From date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGetReport = () => {
    if (!validateForm()) {
      return;
    }

    console.log('Generating report with data:', formData);
    setSuccessMessage(`Direct team report generated successfully for ${formData.loginId} from ${formData.joiningFrom} to ${formData.joiningTo}!`);
    
    // Clear success message after 5 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 5000);
  };

  const handleReset = () => {
    setFormData({
      loginId: '',
      joiningFrom: '',
      joiningTo: '',
    });
    setErrors({});
    setSuccessMessage('');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <Assessment sx={{ color: '#1976d2', fontSize: 32 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1976d2' }}>
          Direct Team Report
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 4, 
            borderRadius: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle sx={{ fontSize: 20 }} />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #90caf9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Group sx={{ color: '#1976d2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Total Team Members
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {loginIds.length}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e8f5e9', 
            border: '1px solid #c8e6c9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <TrendingUp sx={{ color: '#2e7d32', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Active Reports
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  12
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#fff3e0', 
            border: '1px solid #ffcc02',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Business sx={{ color: '#f57c00', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Departments
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  5
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Card */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, maxWidth: 800, mx: 'auto' }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Description sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Generate Direct Team Report
            </Typography>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Info sx={{ fontSize: 20 }} />
              Select login ID and date range to generate comprehensive team report
            </Box>
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.loginId}>
                <InputLabel>Login ID *</InputLabel>
                <Select
                  value={formData.loginId}
                  label="Login ID *"
                  onChange={(e) => handleFormChange('loginId', e.target.value)}
                  required
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                >
                  {loginIds.map((loginId) => (
                    <MenuItem key={loginId} value={loginId}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Person sx={{ fontSize: 16, color: '#666' }} />
                        {loginId}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.loginId && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                    {errors.loginId}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Joining From *"
                type="date"
                value={formData.joiningFrom}
                onChange={(e) => handleFormChange('joiningFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                error={!!errors.joiningFrom}
                helperText={errors.joiningFrom}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Joining To *"
                type="date"
                value={formData.joiningTo}
                onChange={(e) => handleFormChange('joiningTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                error={!!errors.joiningTo}
                helperText={errors.joiningTo}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ 
                display: 'flex', 
                gap: 3, 
                justifyContent: 'center', 
                mt: 3,
                p: 3,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
              }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<GetApp />}
                  onClick={handleGetReport}
                  sx={{ 
                    minWidth: 180,
                    py: 1.5,
                    px: 3,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '16px',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Generate Report
                </Button>
                
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<Refresh />}
                  onClick={handleReset}
                  sx={{ 
                    minWidth: 180,
                    py: 1.5,
                    px: 3,
                    borderRadius: 2,
                    borderWidth: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '16px',
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Reset Form
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Direct;